import { useEffect, useRef, useState } from "react";
import Popover from "@mui/material/Popover";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faNoteSticky,
    faPlus,
    faFloppyDisk,
    faTrashCan,
    faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import { Ring } from "@uiball/loaders";
import { BRAND } from "constants/brand";
import { updateUserNoteService } from "services/user";
import "./UserNote.scss";

const MAX_LENGTH = 1000;

/**
 * Inline admin note attached to a user. Renders a compact chip beside the
 * user's name; clicking opens a popover to add / edit / clear the note.
 *
 * Reused in the users table and the orders list (where the note belongs to
 * the order's linked customer). The note is global to the user, so editing it
 * from either surface updates it everywhere.
 *
 * Props:
 *   userId      number          — required; the user the note belongs to
 *   note        string | null   — current note text
 *   userName    string          — shown in the popover header
 *   editable    boolean         — when false, the note is read-only (no add affordance)
 *   expand      boolean         — when true, show the full note (wraps, no truncation);
 *                                 use on detail pages. Tables leave it false for a
 *                                 single-line truncated chip.
 *   onSaved     (note) => void  — optional; called after a successful save/clear
 *   showMessage (type, head, content) => void — optional toast callback
 */
const UserNote = ({
    userId,
    note,
    userName,
    editable = false,
    expand = false,
    onSaved,
    showMessage,
}) => {
    const [currentNote, setCurrentNote] = useState(note || "");
    const [anchorEl, setAnchorEl] = useState(null);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const textareaRef = useRef(null);

    // Keep the displayed note in sync when the parent re-renders with fresh data.
    useEffect(() => {
        setCurrentNote(note || "");
    }, [note]);

    const open = Boolean(anchorEl);
    const hasNote = currentNote.trim().length > 0;

    const handleOpen = (e) => {
        if (!editable) return;
        e.stopPropagation();
        setDraft(currentNote);
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        if (saving) return;
        setAnchorEl(null);
    };

    const persist = async (value) => {
        setSaving(true);
        try {
            const response = await updateUserNoteService(userId, value);
            const saved = response?.user?.note ?? "";
            setCurrentNote(saved);
            onSaved?.(saved);
            showMessage?.("success", "تم الحفظ", "تم حفظ الملاحظة بنجاح");
            setAnchorEl(null);
        } catch (err) {
            showMessage?.("error", "هناك خطأ", err?.response?.data?.message || "تعذر حفظ الملاحظة");
        } finally {
            setSaving(false);
        }
    };

    const handleSave = () => persist(draft.trim());
    const handleClear = () => persist("");

    // Read-only with no note: render nothing so the row stays clean.
    if (!editable && !hasNote) return null;

    return (
        <div className="user-note" onClick={(e) => e.stopPropagation()}>
            {hasNote ? (
                expand ? (
                    // Detail pages: plain text (matches the other "label: value"
                    // rows) with a small inline edit pencil for admins.
                    <span className="user-note__text">
                        <span className="user-note__text-value">{currentNote}</span>
                        {editable ? (
                            <button
                                type="button"
                                className="user-note__edit-icon"
                                onClick={handleOpen}
                                title="تعديل الملاحظة"
                                aria-label="تعديل الملاحظة"
                            >
                                <FontAwesomeIcon icon={faPenToSquare} />
                            </button>
                        ) : null}
                    </span>
                ) : (
                    <button
                        type="button"
                        className={`user-note__chip ${editable ? "is-editable" : ""}`}
                        title={editable ? `${currentNote}\n(اضغط للتعديل)` : currentNote}
                        onClick={handleOpen}
                        disabled={!editable}
                    >
                        <FontAwesomeIcon icon={faNoteSticky} className="user-note__chip-icon" />
                        <span className="user-note__chip-text">{currentNote}</span>
                    </button>
                )
            ) : (
                <button
                    type="button"
                    className="user-note__add"
                    onClick={handleOpen}
                    title="إضافة ملاحظة"
                    aria-label="إضافة ملاحظة"
                >
                    <FontAwesomeIcon icon={faPlus} />
                </button>
            )}

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                TransitionProps={{
                    onEntered: () => textareaRef.current?.focus(),
                }}
                slotProps={{ paper: { className: "user-note__popover" } }}
            >
                <div className="user-note__editor" dir="rtl">
                    <div className="user-note__editor-head">
                        <FontAwesomeIcon icon={faNoteSticky} color={BRAND} />
                        <span>ملاحظة{userName ? ` — ${userName}` : ""}</span>
                    </div>

                    <textarea
                        ref={textareaRef}
                        className="user-note__textarea"
                        value={draft}
                        maxLength={MAX_LENGTH}
                        placeholder="اكتب ملاحظة عن الحساب..."
                        onChange={(e) => setDraft(e.target.value)}
                        rows={4}
                        disabled={saving}
                    />
                    <div className="user-note__count">
                        {draft.length} / {MAX_LENGTH}
                    </div>

                    <div className="user-note__actions">
                        {hasNote ? (
                            <button
                                type="button"
                                className="user-note__btn user-note__btn--danger"
                                onClick={handleClear}
                                disabled={saving}
                            >
                                <FontAwesomeIcon icon={faTrashCan} />
                                <span>حذف</span>
                            </button>
                        ) : (
                            <span />
                        )}
                        <div className="user-note__actions-right">
                            <button
                                type="button"
                                className="user-note__btn user-note__btn--ghost"
                                onClick={handleClose}
                                disabled={saving}
                            >
                                إلغاء
                            </button>
                            <button
                                type="button"
                                className="user-note__btn user-note__btn--primary"
                                onClick={handleSave}
                                disabled={saving || draft.trim() === currentNote.trim()}
                            >
                                {saving ? (
                                    <Ring size={16} lineWeight={5} speed={2} color="#fff" />
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faFloppyDisk} />
                                        <span>حفظ</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </Popover>
        </div>
    );
};

export default UserNote;
